/**
 * The seating, as a mode is allowed to see it.
 *
 * A mode needs three things and no more: which player a peer is, which peer a player is, and to be
 * told when that changes. It does not get to seat anyone, because seating is the host's and the
 * host's alone, and a mode that could hand out a player number would be a second opinion on who is
 * in the game.
 *
 * Every session has one, single player included, where it holds exactly the host. That is what
 * keeps the roster out of the mode: before this, the game kept its own peer-to-player map that only
 * existed once a match was running, so the lobby and the game each had their own idea of who player
 * three was and nothing made them agree.
 */

import { seatOfPeer, seatOfPlayer } from '@/net/session/seatTable';
import type { Seat, SeatTable } from '@/net/session/seatTable';
import type { PeerId } from '@/net/transportContract';

export interface SeatDirectory {
  seats(): readonly Seat[];
  /** Null for a peer with no seat, which is a peer the host has not admitted. */
  playerIdOf(peerId: PeerId): number | null;
  /** Null while the seat is held for somebody who dropped. */
  peerIdOf(playerId: number): PeerId | null;
  /** Called on every seating change. Returns the unsubscribe function. */
  subscribe(listener: (seats: readonly Seat[]) => void): () => void;
}

export interface SeatDirectorySource {
  /** The one live table, read fresh each time rather than copied. */
  readonly table: () => SeatTable;
  readonly subscribe: (listener: (seats: readonly Seat[]) => void) => () => void;
}

export function createSeatDirectory(source: SeatDirectorySource): SeatDirectory {
  return {
    seats: (): readonly Seat[] => source.table().seats,
    playerIdOf: (peerId: PeerId): number | null =>
      seatOfPeer(source.table(), peerId)?.playerId ?? null,
    peerIdOf: (playerId: number): PeerId | null =>
      seatOfPlayer(source.table(), playerId)?.peerId ?? null,
    subscribe: (listener): (() => void) => source.subscribe(listener),
  };
}

/** A directory that never changes, for a session with one seat and nobody to put in the others. */
export function fixedSeatDirectory(table: SeatTable): SeatDirectory {
  return createSeatDirectory({
    table: (): SeatTable => table,
    subscribe: (): (() => void) => (): void => undefined,
  });
}
